/**
 * Public Copilot API client (Pulse).
 *
 * Wraps the `/api/public/copilot/*` endpoints. Auth is intentionally
 * light — the visitor's identity is a session id minted by the
 * backend and mirrored in `localStorage` (`cv_pub_session_hint`).
 * The id rides on the wire via the `X-Pub-Session` request header
 * (GET / DELETE) or the `session_id` field in the JSON body (POST
 * /chat) — never as a cookie. This keeps every endpoint a "simple"
 * cross-origin request: no `credentials: 'include'`, no preflight
 * coupling to specific origins, no `Access-Control-Allow-Credentials`
 * dance with the worker's wildcard CORS policy.
 *
 * `streamChat` uses fetch directly (not `EventSource`) so the
 * request can carry the JSON body + custom headers, then the backend
 * upgrades the response to text/event-stream.
 */
import { getApiBase } from '../config/api';

const PUB_SESSION_HINT_KEY = 'cv_pub_session_hint';
const PUB_SESSION_HEADER = 'X-Pub-Session';

function readSessionHint() {
  try { return localStorage.getItem(PUB_SESSION_HINT_KEY) || null; } catch { return null; }
}

function writeSessionHint(id) {
  if (!id) return;
  try { localStorage.setItem(PUB_SESSION_HINT_KEY, String(id)); } catch { /* ignore */ }
}

function clearSessionHint() {
  try { localStorage.removeItem(PUB_SESSION_HINT_KEY); } catch { /* ignore */ }
}

function getVisitorId() {
  try {
    if (typeof window !== 'undefined' && window.__cvVisitorId) return String(window.__cvVisitorId);
  } catch { /* swallow */ }
  try { return localStorage.getItem('cv_visitor_id') || ''; } catch { return ''; }
}

function buildHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  // Visitor id keeps the credit bucket stable for anonymous visitors
  // across IPv6 privacy-extension rotations. The worker treats a
  // missing header as "fall back to IP-based bucketing" so older
  // clients keep working unchanged.
  const vid = getVisitorId();
  if (vid && !headers['x-cv-visitor-id'] && !headers['X-CV-Visitor-Id']) {
    headers['x-cv-visitor-id'] = vid;
  }
  return headers;
}

function withSessionHeader(extra = {}, sessionId = null) {
  const sid = sessionId || readSessionHint();
  return sid ? buildHeaders({ ...extra, [PUB_SESSION_HEADER]: sid }) : buildHeaders(extra);
}

/** Fetch the public, auth-free config (kill switch + safe-to-show
 *  rate limits). Used by the global mount point to decide whether
 *  to render the panel at all. */
export async function getPublicCopilotConfig() {
  const r = await fetch(`${getApiBase()}/public/copilot/config`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  return r.json();
}

/** Public list of pages the panel can suggest. Cheap / cacheable. */
export async function listPublicCopilotPages() {
  const r = await fetch(`${getApiBase()}/public/copilot/pages`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  return r.json();
}

/** Restore a session by id (visitor proves ownership by sending the
 *  same id we minted for them via the X-Pub-Session header). */
export async function getPublicCopilotSession(sessionId) {
  if (!sessionId) return { error: 'no_session' };
  const r = await fetch(
    `${getApiBase()}/public/copilot/sessions/${encodeURIComponent(sessionId)}`,
    { headers: withSessionHeader({}, sessionId) }
  );
  return r.json();
}

export async function deletePublicCopilotSession(sessionId) {
  if (!sessionId) return { error: 'no_session' };
  const r = await fetch(
    `${getApiBase()}/public/copilot/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', headers: withSessionHeader({}, sessionId) }
  );
  if (r.ok) clearSessionHint();
  return r.json();
}

/**
 * Stream a chat turn. Returns a controller:
 *   const ctrl = streamPublicCopilotChat({ message, sessionId, pageContext, onEvent });
 *   await ctrl.done; // resolves with the last `done` payload
 *   ctrl.abort();    // cancel
 *
 * `onEvent({ type, data })` is called for every server-sent event. Event
 * types: 'session', 'phase', 'tool_start', 'tool_result', 'assistant',
 * 'rate_limit_exceeded', 'insufficient_credits', 'disabled', 'error', 'done'.
 */
export function streamPublicCopilotChat({
  message,
  sessionId = null,
  pageContext = null,
  turnstileToken = null,
  onEvent,
}) {
  const controller = new AbortController();
  const sid = sessionId || readSessionHint();
  const headers = withSessionHeader({ Accept: 'text/event-stream' }, sid);

  const done = (async () => {
    let lastDonePayload = null;
    try {
      const response = await fetch(`${getApiBase()}/public/copilot/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message,
          session_id: sid,
          page_context: pageContext,
          turnstile_token: turnstileToken,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        let detail = '';
        try { detail = await response.text(); } catch { /* ignore */ }
        const err = { status: response.status, message: detail || `Pulse HTTP ${response.status}` };
        if (typeof onEvent === 'function') {
          try { onEvent({ type: 'error', data: { message: err.message, status: response.status } }); } catch { /* ignore */ }
        }
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done: readerDone } = await reader.read();
        if (readerDone) break;
        buffer += decoder.decode(value, { stream: true });

        let blank;
        while ((blank = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, blank);
          buffer = buffer.slice(blank + 2);
          const lines = rawEvent.split('\n');
          let eventName = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) eventName = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let parsed = null;
          try {
            parsed = JSON.parse(dataLines.join('\n'));
          } catch {
            parsed = { raw: dataLines.join('\n') };
          }
          if (eventName === 'session' && parsed?.session_id) {
            writeSessionHint(parsed.session_id);
          }
          if (eventName === 'done') lastDonePayload = parsed;
          if (typeof onEvent === 'function') {
            try { onEvent({ type: eventName, data: parsed }); } catch { /* ignore */ }
          }
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        if (typeof onEvent === 'function') {
          try { onEvent({ type: 'aborted', data: {} }); } catch { /* ignore */ }
        }
      } else {
        if (typeof onEvent === 'function') {
          try { onEvent({ type: 'error', data: { message: e?.message || String(e) } }); } catch { /* ignore */ }
        }
        throw e;
      }
    }
    return lastDonePayload;
  })();

  return {
    done,
    abort: () => controller.abort(),
  };
}

/** Save validated visitor contact to D1 (`public_copilot_leads`). Requires analytics migration 0063. */
export async function savePublicCopilotLead(payload) {
  const sid = payload?.session_id || readSessionHint();
  const r = await fetch(`${getApiBase()}/public/copilot/leads`, {
    method: 'POST',
    headers: withSessionHeader({ 'Content-Type': 'application/json' }, sid),
    body: JSON.stringify({
      session_id: sid || undefined,
      page_url: typeof payload?.page_url === 'string' ? payload.page_url : undefined,
      name: payload?.name ?? '',
      company: payload?.company ?? '',
      mobile: payload?.mobile ?? '',
      email: payload?.email ?? '',
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    return { success: false, status: r.status, ...data };
  }
  return { success: true, ...data };
}

/**
 * Anonymous-visitor "open a support ticket" flow used by Pulse.
 *
 * Three steps:
 *   1. requestPulseTicketOtp({ email })          → emails a 12-char OTP.
 *   2. verifyPulseTicketOtp({ email, otp })      → marks email verified for 1h.
 *   3. createPulseTicket({ email, mobile, ... }) → opens conversation + ticket.
 *
 * The OTP step reuses the existing premium-checkout helper on the backend
 * so we don't introduce a second OTP template / quota policy. The
 * `verified:{email}` KV marker (set by step 2) is the gate that step 3
 * checks.
 */
export async function requestPulseTicketOtp(email) {
  const r = await fetch(`${getApiBase()}/public/copilot/ticket/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

export async function verifyPulseTicketOtp(email, otp) {
  const r = await fetch(`${getApiBase()}/public/copilot/ticket/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

export async function createPulseTicket({ email, mobile, name, message, title, page_url } = {}) {
  const sid = readSessionHint();
  const r = await fetch(`${getApiBase()}/public/copilot/ticket/create`, {
    method: 'POST',
    headers: withSessionHeader({ 'Content-Type': 'application/json' }, sid),
    body: JSON.stringify({
      email,
      mobile,
      name: name || '',
      message,
      title: title || '',
      page_url: page_url || (typeof window !== 'undefined' ? window.location.href : ''),
    }),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

/** OTP-gated list of the visitor's existing open tickets keyed by their
 *  verified email. Caller must have completed the request-otp / verify-otp
 *  dance; the server checks the `verified:{email}` KV marker. */
export async function listPulseMyTickets(email, { limit = 5 } = {}) {
  if (!email) return { success: false, error: 'invalid_email' };
  const qs = new URLSearchParams({ email, limit: String(limit) });
  const r = await fetch(`${getApiBase()}/public/copilot/tickets/mine?${qs.toString()}`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

/** Append a follow-up message to an existing ticket from a Pulse visitor.
 *  OTP-gated: server matches `email` against `ticket.customer_email` AND
 *  requires a fresh `verified:{email}` KV marker. */
export async function appendToPulseTicket(ticketId, { email, body } = {}) {
  if (!ticketId) return { success: false, error: 'invalid_ticket' };
  const r = await fetch(`${getApiBase()}/public/copilot/tickets/${encodeURIComponent(ticketId)}/follow-up`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ email, body: String(body || '') }),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

/** Public mirror of submitRating for Pulse — requires the visitor's session
 *  id (the same one that owns the conversation). Feeds the same harvest
 *  pipeline as the authed endpoint. */
export async function submitPulseConversationRating(conversationId, { rating, comment, tags } = {}) {
  if (!conversationId) return { success: false, error: 'invalid_conversation' };
  const sid = readSessionHint();
  const r = await fetch(
    `${getApiBase()}/public/copilot/conversations/${encodeURIComponent(conversationId)}/rating`,
    {
      method: 'POST',
      headers: withSessionHeader({ 'Content-Type': 'application/json' }, sid),
      body: JSON.stringify({
        rating,
        comment: comment || null,
        tags: Array.isArray(tags) ? tags : null,
      }),
    }
  );
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

/** Get the visitor's existing rating for a Pulse conversation (or null). */
export async function getPulseConversationRating(conversationId) {
  if (!conversationId) return { success: false, error: 'invalid_conversation' };
  const sid = readSessionHint();
  const r = await fetch(
    `${getApiBase()}/public/copilot/conversations/${encodeURIComponent(conversationId)}/rating`,
    { method: 'GET', headers: withSessionHeader({}, sid) }
  );
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...data };
}

/** Thumbs + 1–5 star rating for an assistant message (visitor must own session). */
export async function submitPublicCopilotMessageFeedback(sessionId, messageId, body) {
  if (!sessionId || !messageId) return { error: 'missing ids' };
  const r = await fetch(
    `${getApiBase()}/public/copilot/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    {
      method: 'POST',
      headers: withSessionHeader({ 'Content-Type': 'application/json' }, sessionId),
      body: JSON.stringify(body || {}),
    }
  );
  return r.json();
}

export const publicCopilotSessionHint = {
  read: readSessionHint,
  write: writeSessionHint,
  clear: clearSessionHint,
};
